import { Component, OnInit, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map, startWith, tap, finalize } from 'rxjs/operators';
import { showSpinner, hideSpinner, getCurrentUser } from 'src/app/core/services/functions.service';
import { NotificationHelper } from 'src/app/core/services/helpers/notification.helper';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { OpcionesCargaDialogComponent } from '../opciones-carga-dialog/opciones-carga-dialog.component';
import { DecimalPipe } from '@angular/common';
import { DetalleBonificacionDialogComponent } from '../detalle-bonificacion-dialog/detalle-bonificacion-dialog.component';

import { BonificacionesService, Bonificacion, ConceptoBonificacion, BulkLoadResponse } from '../../services/bonificaciones.service';
import { VendedorService, Vendedor } from '../../../metavendedor/services/vendedor.service';
import { PeriodosmetasService, PeriodoMetas, ApiResponse } from '../../../periodosmetas/services/periodosmetas.service';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-bonificacionesextras',
  templateUrl: './bonificacionesextras.component.html',
  styleUrls: ['./bonificacionesextras.component.scss'],
  providers: [DecimalPipe]
})
export class BonificacionesextrasComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  bonificacionForm: FormGroup;
  searchControl = new FormControl('');
  vendedorControl = new FormControl('');
  isEditing = false;
  isLoading = false;
  editingBonificacionId: number | null = null;
  selectedFile: File | null = null;

  periodosAbiertos: PeriodoMetas[] = [];
  vendedores: Vendedor[] = [];
  vendedoresFiltrados: Observable<Vendedor[]>;
  conceptos: ConceptoBonificacion[] = [];

  // Lista de conceptos protegidos que no se pueden editar ni eliminar
  protected readonly CONCEPTOS_PROTEGIDOS = ['001', '002', '003', '004', '005', '006'];

  displayedColumns: string[] = [
    'periodo',
    'c_concepto',
    'c_vend',
    'valor',
    'fecha_creacion',
    'acciones'
  ];
  columnasConceptos: string[] = ['codigo', 'descripcion'];
  columnasCamposRequeridos: string[] = ['campo', 'formato', 'ejemplo', 'descripcion'];
  dataSource = new MatTableDataSource<Bonificacion>();

  camposRequeridos = [
    { campo: 'periodo', formato: 'YYYYMM', ejemplo: '202501', descripcion: 'Periodo de la bonificación', requerido: 'Si'},
    { campo: 'c_vend', formato: 'Texto', ejemplo: 'V001', descripcion: 'Código del vendedor',requerido: 'Si' },
    { campo: 'c_concepto', formato: 'Texto', ejemplo: 'BON01', descripcion: 'Código del concepto',requerido: 'Si' },
    { campo: 'valor', formato: 'Número', ejemplo: '100000', descripcion: 'Valor de la bonificación',requerido: 'Si' },
    { campo: 'observaciones', formato: 'Texto', ejemplo: 'Nueva observación', descripcion: 'Observaciones de la bonificación',requerido: 'No' }
  ];
  columnasTabla = ['campo', 'formato', 'ejemplo', 'descripcion'];

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private notificationService: NotificationHelper,
    private bonificacionesService: BonificacionesService,
    private periodosService: PeriodosmetasService,
    private vendedorService: VendedorService,
    private cd: ChangeDetectorRef,
    private decimalPipe: DecimalPipe
  ) {
    this.initForm();
    
    // Sincronizar el vendedorControl con el campo c_vend del formulario
    this.vendedorControl.valueChanges.subscribe(vendedor => {
      if (vendedor && typeof vendedor === 'object') {
        this.bonificacionForm.patchValue({
          c_vend: vendedor.codigo_vendedor
        }, { emitEvent: true });
      } else {
        this.bonificacionForm.patchValue({
          c_vend: null
        }, { emitEvent: true });
      }
      // Forzar la validación del formulario
      this.bonificacionForm.get('c_vend')?.updateValueAndValidity();
      this.cd.detectChanges();
    });
  }

  ngOnInit(): void {
    this.cargarPeriodosAbiertos();
    this.cargarVendedores();
    this.cargarBonificaciones();
    this.loadConceptos();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  initForm(): void {
    this.bonificacionForm = this.fb.group({
      periodo: ['', Validators.required],
      c_concepto: ['', Validators.required],
      c_vend: ['', Validators.required],
      valor: ['', [Validators.required]],
      observaciones: ['']
    });
  }

  cargarPeriodosAbiertos(): void {
    showSpinner();
    this.periodosService.getPeriodosAbiertos('JA')
      .pipe(finalize(() => hideSpinner()))
      .subscribe({
        next: (response: ApiResponse<PeriodoMetas[]>) => {
          if (response?.data) {
            this.periodosAbiertos = response.data;
            if (this.periodosAbiertos.length > 0) {
              this.bonificacionForm.patchValue({
                periodo: this.periodosAbiertos[0].periodo
              });
            }
          }
        },
        error: (error) => {
          this.notificationService.errorMessage('Error al cargar los periodos: ' + error.message);
          this.periodosAbiertos = [];
        }
      });
  }

  cargarVendedores(): void {
    showSpinner();
    this.vendedorService.getVendedores('JA')
      .pipe(
        finalize(() => hideSpinner())
      )
      .subscribe({
        next: (vendedores) => {
          this.vendedores = vendedores;
          this.configurarFiltroVendedores();
        },
        error: (error) => {
          this.notificationService.errorMessage('Error al cargar los vendedores: ' + error.message);
        }
      });
  }

  cargarBonificaciones(): void {
    showSpinner();
    this.bonificacionesService.getBonificaciones('JA')
      .pipe(
        finalize(() => {
          hideSpinner();
          this.cd.detectChanges();
        })
      )
      .subscribe({
        next: (bonificaciones) => {
          this.dataSource.data = bonificaciones || [];
          this.cd.detectChanges();
          this.notificationService.successMessage('Datos cargados correctamente');
        },
        error: (error) => {
          let errorMessage = 'Error desconocido al cargar las bonificaciones';
          
          if (typeof error === 'object' && error !== null) {
            if (error.message) {
              errorMessage = error.message;
            }
          }

          this.notificationService.errorMessage(errorMessage);
          this.dataSource.data = [];
          this.cd.detectChanges();
        }
      });
  }

  loadConceptos() {
    showSpinner();
    this.bonificacionesService.getConceptosBonificacion('JA')
      .pipe(
        finalize(() => {
          hideSpinner();
        })
      )
      .subscribe({
        next: (conceptos) => {
          this.conceptos = conceptos;
          // Forzar detección de cambios
          this.cd.detectChanges();
        },
        error: (error) => {
          this.notificationService.errorMessage('Error al cargar los conceptos de bonificación');
        }
      });
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.selectedFile = file;
    }
  }

  async procesarArchivo(): Promise<void> {
    if (!this.selectedFile) {
      return;
    }

    try {
      // Leer el archivo Excel
      const workbook = await this.leerArchivoExcel(this.selectedFile);
      const primeraHoja = workbook.Sheets[workbook.SheetNames[0]];
      
      // Convertir a JSON
      const rows = XLSX.utils.sheet_to_json(primeraHoja, { 
        header: 0, 
        raw: false,
        defval: '' 
      }) as Record<string, string>[];
      
      if (rows.length === 0) {
        this.notificationService.errorMessage('El archivo está vacío');
        return;
      }
      
      // Validar columnas requeridas
      const columnas = Object.keys(rows[0]);
      const columnasRequeridas = ['periodo', 'c_vend', 'c_concepto', 'valor', 'observaciones'];
      const columnasFaltantes = columnasRequeridas.filter(col => 
        !columnas.map(c => c.toLowerCase()).includes(col.toLowerCase())
      );
      
      if (columnasFaltantes.length > 0) {
        this.notificationService.errorMessage(`El archivo debe contener las columnas: ${columnasRequeridas.join(', ')}`);
        return;
      }
      
      // Verificar si hay columnas adicionales no permitidas
      const columnasNoPermitidas = columnas.filter(col => 
        !columnasRequeridas.map(c => c.toLowerCase()).includes(col.toLowerCase())
      );
      
      if (columnasNoPermitidas.length > 0) {
        this.notificationService.errorMessage(`El archivo contiene columnas no permitidas: ${columnasNoPermitidas.join(', ')}. Solo se permiten: ${columnasRequeridas.join(', ')}`);
        return;
      }

      // Validar que solo haya un periodo
      const periodos = new Set(rows.map(row => String(row.periodo || '').trim()));
      if (periodos.size > 1) {
        this.notificationService.errorMessage('Solo se permite el cargue de un periodo por vez');
        return;
      }

      // Validar que no haya números negativos en el campo de valor
      const registrosConValoresNegativos = rows.filter(row => {
        const valor = Number(row.valor);
        return !isNaN(valor) && valor < 0;
      });

      if (registrosConValoresNegativos.length > 0) {
        this.notificationService.errorMessage('No se permiten valores negativos en el campo de valor');
        return;
      }
      
      // Validar que no haya valores cero en el campo de valor
      const registrosConValoresCero = rows.filter(row => {
        const valor = Number(row.valor);
        return !isNaN(valor) && valor === 0;
      });

      if (registrosConValoresCero.length > 0) {
        this.notificationService.errorMessage('No se permiten valores cero en el campo de valor');
        return;
      }

      // Obtener el periodo del archivo
      const periodo = Array.from(periodos)[0];

      // Validar formato del periodo
      if (!periodo.match(/^\d{6}$/)) {
        this.notificationService.errorMessage('El período debe tener formato YYYYMM');
        return;
      }

      // Validar que el periodo exista y no esté cerrado
      const periodoExistente = this.periodosAbiertos.find(p => p.periodo === periodo);
      if (!periodoExistente) {
        this.notificationService.errorMessage(`El periodo ${periodo} no existe o está cerrado`);
        return;
      }

      // Mostrar diálogo de opciones con el periodo detectado
      const dialogRef = this.dialog.open(OpcionesCargaDialogComponent, {
        width: '600px',
        disableClose: true,
        data: {
          nombreArchivo: this.selectedFile.name,
          periodo: periodo
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result?.confirmado) {
          showSpinner();
          const currentUser = getCurrentUser();
          
          // Agregar campos adicionales a cada registro
          const rowsConCamposAdicionales = rows.map(row => ({
            ...row,
            c_emp: currentUser.c_emp,
            usuario_creacion: currentUser.usuario_aplicativo,
            usuario_modificacion: currentUser.usuario_aplicativo,
            estado: 'A'
          }));

          if (result.tipo === 'REEMPLAZAR') {
            this.bonificacionesService.bulkLoad('JA',rowsConCamposAdicionales, true, periodo)
              .pipe(finalize(() => hideSpinner()))
              .subscribe({
                next: (response) => {
                  this.notificationService.successMessage(response.mensaje);
                  this.cancelarSeleccionArchivo();
                  this.cargarBonificaciones();
                },
                error: (error) => {
                  this.notificationService.errorMessage(error.message);
                }
              });
          } else {
            this.bonificacionesService.bulkLoad('JA',rowsConCamposAdicionales, false)
              .pipe(finalize(() => hideSpinner()))
              .subscribe({
                next: (response) => {
                  this.notificationService.successMessage(response.mensaje);
                  this.cancelarSeleccionArchivo();
                  this.cargarBonificaciones();
                },
                error: (error) => {
                  this.notificationService.errorMessage(error.message);
                }
              });
          }
        } else {
          this.cancelarSeleccionArchivo();
        }
      });
    } catch (error) {
      this.notificationService.errorMessage(error instanceof Error ? error.message : 'Error al procesar el archivo');
    }
  }

  private leerArchivoExcel(file: File): Promise<XLSX.WorkBook> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const workbook = XLSX.read(reader.result, { type: 'binary' });
          resolve(workbook);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Error al leer el archivo Excel'));
      reader.readAsBinaryString(file);
    });
  }

  cancelarSeleccionArchivo(): void {
    this.selectedFile = null;
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  onSubmit() {
    if (this.bonificacionForm.valid && this.vendedorControl.value) {
      const formData = this.bonificacionForm.getRawValue(); // Obtiene valores incluso de campos deshabilitados
      
      // Crear el objeto de bonificación
      const currentUser = getCurrentUser();
      const bonificacion: Partial<Bonificacion> = {
        c_emp: currentUser.c_emp, // Empresa Jamar
        periodo: formData.periodo,
        c_concepto: formData.c_concepto,
        c_vend: formData.c_vend,
        valor: Number(formData.valor), // Asegurar que sea número
        observaciones: formData.observaciones || '',
        usuario_creacion: currentUser.usuario_aplicativo,
        usuario_modificacion: currentUser.usuario_aplicativo,
        estado: 'A' // Mantener el estado activo
      };


      showSpinner();
      if (this.isEditing && this.editingBonificacionId !== null) {
        this.bonificacionesService.updateBonificacion(this.editingBonificacionId, bonificacion, 'JA')
          .pipe(finalize(() => hideSpinner()))
          .subscribe({
            next: () => {
              this.notificationService.successMessage('Bonificación actualizada correctamente');
              this.resetForm();
              this.cargarBonificaciones();
            },
            error: (error) => {
              let errorMessage = 'Error al actualizar la bonificación';
              if (error.error && error.error.detail) {
                errorMessage += ': ' + error.error.detail;
              }
              this.notificationService.errorMessage(errorMessage);
            }
          });
      } else {
        this.bonificacionesService.createBonificacion(bonificacion, 'JA')
          .pipe(finalize(() => hideSpinner()))
          .subscribe({
            next: () => {
              this.notificationService.successMessage('Bonificación creada correctamente');
              this.resetForm();
              this.cargarBonificaciones();
            },
            error: (error) => {
              let errorMessage = 'Error al crear la bonificación';
              if (error.error && error.error.detail) {
                errorMessage += ': ' + error.error.detail;
              }
              this.notificationService.errorMessage(errorMessage);
            }
          });
      }
    } else {
      this.notificationService.warningMessage('Por favor complete todos los campos requeridos correctamente');
    }
  }

  resetForm(): void {
    this.bonificacionForm.reset();
    this.vendedorControl.reset();
    this.vendedorControl.enable(); // Habilitar el control del vendedor
    this.bonificacionForm.get('periodo')?.enable(); // Habilitar el campo periodo
    this.isEditing = false;
    this.editingBonificacionId = null;
  }

  onCancel(): void {
    this.vendedorControl.enable(); // Habilitar el control del vendedor
    this.isEditing = false;
    this.editingBonificacionId = null;
    this.resetForm();
  }

  onDelete(id: number): void {

    const currentUser = getCurrentUser();
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Confirmar eliminación',
        message: '¿Está seguro que desea eliminar esta bonificación?'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        showSpinner();
        this.bonificacionesService.deleteBonificacion(id, 'JA')
          .pipe(finalize(() => hideSpinner()))
          .subscribe({
            next: () => {
              this.notificationService.successMessage('Bonificación eliminada correctamente');
              this.cargarBonificaciones();
            },
            error: (error) => {
              let errorMessage = 'Error al eliminar la bonificación';
              if (error.error && error.error.detail) {
                errorMessage += ': ' + error.error.detail;
              }
              this.notificationService.errorMessage(errorMessage);
            }
          });
      }
    });
  }

  isPeriodoAbierto(periodo: string): boolean {
    return this.periodosAbiertos.some(p => p.periodo === periodo);
  }

  // Método para verificar si un concepto está protegido
  isConceptoProtegido(c_concepto: string): boolean {
    return this.CONCEPTOS_PROTEGIDOS.includes(c_concepto);
  }

  // Método para verificar si se puede editar una bonificación
  canEdit(bonificacion: Bonificacion): boolean {
    return !this.isConceptoProtegido(bonificacion.c_concepto);
  }

  // Método para verificar si se puede eliminar una bonificación
  canDelete(bonificacion: Bonificacion): boolean {
    return !this.isConceptoProtegido(bonificacion.c_concepto);
  }

  editarBonificacion(bonificacion: any) {
    if (!this.isPeriodoAbierto(bonificacion.periodo)) {
      this.notificationService.warningMessage('No se puede editar un registro de un periodo cerrado');
      return;
    }
    if (this.isConceptoProtegido(bonificacion.c_concepto)) {
      return;
    }
    this.isEditing = true;
    this.editingBonificacionId = bonificacion.id;
    
    // Establecer el vendedor seleccionado
    const vendedor = this.vendedores.find(v => v.codigo_vendedor === bonificacion.c_vend);
    if (vendedor) {
      this.vendedorControl.setValue(vendedor);
      this.vendedorControl.disable(); // Deshabilitar el control del vendedor
    }

    // Actualizar el formulario con los datos de la bonificación
    this.bonificacionForm.patchValue({
      periodo: bonificacion.periodo,
      c_concepto: bonificacion.c_concepto,
      c_vend: bonificacion.c_vend,
      valor: bonificacion.valor,
      observaciones: bonificacion.observaciones
    });

    // Deshabilitar el campo periodo
    this.bonificacionForm.get('periodo')?.disable();
  }

  eliminarBonificacion(bonificacion: any) {
    if (!this.isPeriodoAbierto(bonificacion.periodo)) {
      this.notificationService.warningMessage('No se puede eliminar un registro de un periodo cerrado');
      return;
    }
    if (this.isConceptoProtegido(bonificacion.c_concepto)) {
      return;
    }
    const currentUser = getCurrentUser();
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Confirmar eliminación',
        message: '¿Está seguro que desea eliminar esta bonificación?'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        showSpinner();
        this.bonificacionesService.deleteBonificacion(bonificacion.id, 'JA')
          .pipe(finalize(() => hideSpinner()))
          .subscribe({
            next: () => {
              this.notificationService.successMessage('Bonificación eliminada correctamente');
              this.cargarBonificaciones();
            },
            error: (error) => {
              let errorMessage = 'Error al eliminar la bonificación';
              if (error.error && error.error.detail) {
                errorMessage += ': ' + error.error.detail;
              }
              this.notificationService.errorMessage(errorMessage);
            }
          });
      }
    });
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filterPredicate = (data: Bonificacion, filter: string) => {
      const searchStr = filter.toLowerCase();
      const conceptoDescripcion = this.getConceptoDescripcion(data.c_concepto).toLowerCase();
      return data.periodo.toLowerCase().includes(searchStr) ||
             data.c_concepto.toLowerCase().includes(searchStr) ||
             conceptoDescripcion.includes(searchStr) ||
             data.c_vend.toLowerCase().includes(searchStr) ||
             data.valor.toString().includes(searchStr);
    };
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  mostrarListaVendedores(): void {
    // Forzar que se muestre el autocomplete con la lista completa
    this.vendedorControl.setValue('');
    const inputElement = document.querySelector('input[matInput]') as HTMLInputElement;
    if (inputElement) {
      inputElement.focus();
    }
  }

  getConceptoDescripcion(concepto: string): string {
    const conceptoBonificacion = this.conceptos.find(c => c.codigo === concepto);
    return conceptoBonificacion ? conceptoBonificacion.descripcion : '';
  }

  getVendedorNombre(c_vend: string): string {
    const vendedor = this.vendedores.find(v => v.codigo_vendedor === c_vend);
    return vendedor ? vendedor.nombre_vendedor : '';
  }

  verDetalles(bonificacion: Bonificacion) {
    // Obtener la descripción del concepto
    const conceptoDescripcion = this.getConceptoDescripcion(bonificacion.c_concepto);

    // Obtener el nombre del vendedor
    const vendedorNombre = this.getVendedorNombre(bonificacion.c_vend);

    this.dialog.open(DetalleBonificacionDialogComponent, {
      width: '700px',
      data: {
        bonificacion,
        conceptoDescripcion,
        vendedorNombre
      }
    });
  }

  onValorInput(event: any): void {
    let value = event.target.value;
    value = value.replace(/[^\d-]/g, '');

    if (value.split('-').length > 2) {
      value = value.replace(/-/g, '');
      if (value.length > 0) {
        value = '-' + value;
      }
    }

    if (value.includes('-') && value.indexOf('-') !== 0) {
      value = '-' + value.replace(/-/g, '');
    }

    const numeroValue = value === '-' ? '' : Number(value);
    this.bonificacionForm.get('valor')?.setValue(numeroValue);
  }

  configurarFiltroVendedores(): void {
    this.vendedoresFiltrados = this.vendedorControl.valueChanges.pipe(
      startWith(''),
      map(value => {
        const nombre = typeof value === 'string' ? value : value?.nombre_vendedor;
        return nombre ? this._filtrarVendedores(nombre) : this.vendedores;
      })
    );
  }

  private _filtrarVendedores(nombre: string): Vendedor[] {
    const filterValue = nombre.toLowerCase();
    return this.vendedores.filter(vendedor =>
      vendedor.nombre_vendedor.toLowerCase().includes(filterValue) ||
      vendedor.codigo_vendedor.toLowerCase().includes(filterValue)
    );
  }

  displayVendedor(vendedor: Vendedor): string {
    return vendedor ? `${vendedor.codigo_vendedor} - ${vendedor.nombre_vendedor}` : '';
  }
}
